import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, LayoutDashboard, BookOpen, Compass, Target, Bug, TrendingUp, User, Settings, Zap } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="brand-logo-link">
    <div className="w-9 h-9 rounded-xl bg-emerald-700 text-white flex items-center justify-center font-display font-extrabold">E</div>
    <div className="leading-tight">
      <div className="font-display font-extrabold text-slate-900">English Ready</div>
      <div className="text-xs text-emerald-700 -mt-1">englishready.online</div>
    </div>
  </Link>
);

export function PublicLayout() {
  const [open, setOpen] = React.useState(false);
  const { user } = useAuth();
  const nav = [
    { to: "/how-it-works", label: "How It Works" },
    { to: "/country/AU", label: "Australia" },
    { to: "/country/CA", label: "Canada" },
    { to: "/country/UK", label: "UK" },
    { to: "/pricing", label: "Pricing" },
    { to: "/about", label: "About" },
    { to: "/faq", label: "FAQ" },
  ];
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}-link`}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? "text-emerald-700" : "text-slate-700 hover:text-slate-900"}`
                }>{n.label}</NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <Link to="/dashboard"><Button className="rounded-full" data-testid="nav-dashboard-btn">Go to Dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link" className="text-sm font-medium text-slate-700 hover:text-slate-900 px-3">Log in</Link>
                <Link to="/register"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="nav-start-free-btn">Start Learning Free</Button></Link>
              </>
            )}
          </div>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-3 flex flex-col gap-2">
              {nav.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-slate-700" data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g,'-')}-link`}>{n.label}</Link>
              ))}
              <div className="pt-2 border-t flex flex-col gap-2">
                {user ? (
                  <Link to="/dashboard" onClick={() => setOpen(false)}><Button className="w-full rounded-full">Go to Dashboard</Button></Link>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)}><Button variant="outline" className="w-full rounded-full">Log in</Button></Link>
                    <Link to="/register" onClick={() => setOpen(false)}><Button className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800">Start Free</Button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
      <main className="flex-1"><Outlet /></main>
      <footer className="border-t border-slate-200 bg-slate-50 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
          <div className="col-span-2">
            <Logo />
            <p className="mt-4 text-slate-600 max-w-sm">Practise real conversations, learn useful English and prepare for everyday situations in Australia, Canada and the UK.</p>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-3">Product</div>
            <ul className="space-y-2 text-slate-600">
              <li><Link to="/how-it-works">How It Works</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/contact">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-3">Guides</div>
            <ul className="space-y-2 text-slate-600">
              <li><Link to="/english-for-migrants/australia" data-testid="footer-guide-au">English for migrants in Australia</Link></li>
              <li><Link to="/english-for-migrants/canada" data-testid="footer-guide-ca">English for migrants in Canada</Link></li>
              <li><Link to="/english-for-migrants/uk" data-testid="footer-guide-uk">English for migrants in the UK</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-3">Legal</div>
            <ul className="space-y-2 text-slate-600">
              <li><Link to="/legal/privacy">Privacy Policy</Link></li>
              <li><Link to="/legal/terms">Terms of Service</Link></li>
              <li><Link to="/legal/refund">Refund Policy</Link></li>
              <li><Link to="/legal/cookies">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 py-6 px-4 text-xs text-center text-slate-500">
          English Ready is an educational language-learning and communication-practice platform. It does not provide immigration, legal, medical or financial advice.
        </div>
      </footer>
    </div>
  );
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const links = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/learn", label: "Learn", icon: BookOpen },
    { to: "/missions", label: "Missions", icon: Target },
    { to: "/drills", label: "Daily Drill", icon: Zap },
    { to: "/mistakes", label: "Mistakes", icon: Bug },
    { to: "/progress", label: "Progress", icon: TrendingUp },
  ];
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <nav className="hidden md:flex items-center gap-1">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`app-nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${isActive ? "bg-emerald-50 text-emerald-800" : "text-slate-700 hover:bg-slate-100"}`
                  }>
                  <l.icon size={16}/>{l.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Link to="/admin"><Button variant="outline" size="sm" className="rounded-full" data-testid="nav-admin-btn"><Settings size={14} className="mr-1"/>Admin</Button></Link>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-full bg-emerald-700 text-white flex items-center justify-center font-semibold" data-testid="user-menu-btn">
                  {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-xs text-slate-500">{user?.email}</div>
                <DropdownMenuSeparator/>
                <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile"><User size={14} className="mr-2"/>Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/pricing")} data-testid="menu-upgrade">Upgrade plan</DropdownMenuItem>
                <DropdownMenuSeparator/>
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout"><LogOut size={14} className="mr-2"/>Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="md:hidden border-t border-slate-200 bg-white overflow-x-auto no-scrollbar">
          <div className="px-2 flex gap-1">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className={({isActive}) =>
                `whitespace-nowrap px-3 py-2 text-xs font-medium ${isActive ? "text-emerald-800 border-b-2 border-emerald-700" : "text-slate-600"}`
              }>{l.label}</NavLink>
            ))}
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><Outlet /></main>
    </div>
  );
}

export function ProtectedRoute({ children, requireOnboarding = true, adminOnly = false }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  React.useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    if (adminOnly && user.role !== "admin") { nav("/dashboard", { replace: true }); return; }
    if (requireOnboarding && !user.onboarding_completed && user.role !== "admin") {
      nav("/onboarding", { replace: true });
    }
  }, [user, loading, adminOnly, requireOnboarding, nav]);
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return null;
  return children;
}
